import { maxLength } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { IconVideo, Stack, TextField } from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  VIDEO_PRESSED,
  VIDEO_REMOVE_PRESSED,
  VIDEO_VALIDATION_SUCCEEDED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import {
  youTubeUrl,
  validateField,
  type ValidateFieldResult,
} from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateVideoUrl } from 'src/app/v1/state/modules/formData/videoUrl';
import { FieldHeading } from 'src/app/v1/views/components/FieldHeading';
import { HintText } from 'src/app/v1/views/shared/HintText';
import type { AppState } from 'src/types/appState';
import type { VideoRef } from 'src/types/formFields';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';
import { VideoPreview } from './components/VideoPreview';
import { mapVideoURL } from './utils/mapVideoURL';

export const Video = forwardRef((_props, ref: Ref<VideoRef>) => {
  const { videoUrl } = useSelector(
    (state: AppState) => ({
      videoUrl: state.formData.videoUrl,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();

  const [validationMessage, setValidationMessage] =
    useState<ValidateFieldResult>(null);
  const [inputValue, setInputValue] = useState(videoUrl);

  const { getLanguage } = useConfig();
  const { t } = useTranslations(translations);

  useEffect(() => {
    setInputValue(videoUrl ?? '');
  }, [videoUrl]);

  useImperativeHandle(ref, () => ({
    handleValidation: () => handleInputValidation(inputValue),
  }));

  const handleInputValidation = (value: string) => {
    const validationResult = validateField(
      {
        field: 'video',
        validators: [youTubeUrl, maxLength],
        validatorProps: { max: 255 },
        value,
      },
      false,
      getLanguage(),
    );

    setValidationMessage(validationResult);

    return validationResult;
  };

  const handleOnFocus = () => {
    const eventData: TrackingEvent = {
      eventName: VIDEO_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValidationMessage(null);
    setInputValue(value);
    handleInputValidation(value);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);

    if (!Boolean(value)) {
      return;
    }

    const errorMessage = handleInputValidation(value);
    if (!errorMessage) {
      const mappedURL = mapVideoURL(value);
      if (mappedURL) {
        dispatch(actionUpdateVideoUrl(mappedURL));

        const eventData: TrackingEvent = {
          eventName: VIDEO_VALIDATION_SUCCEEDED,
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      }
    }
  };

  const handleReset = () => {
    setValidationMessage(null);
    setInputValue('');
    dispatch(actionUpdateVideoUrl(''));

    const eventData: TrackingEvent = {
      eventName: VIDEO_REMOVE_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  const hasVideo = Boolean(videoUrl);

  return (
    <Stack space="small">
      <FieldHeading
        heading={t('video-label', {
          HintText: (children) => (
            <HintText isHeading={false} text={children.toString()} />
          ),
        })}
        subHeading={!hasVideo ? t('video-secondary-text') : undefined}
      />
      {hasVideo ? (
        <VideoPreview videoUrl={inputValue} handleReset={handleReset} />
      ) : (
        <TextField
          id="VideoUrlTextField"
          aria-label={t('video-aria-label')}
          value={inputValue}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          tone={validationMessage ? 'critical' : 'neutral'}
          message={validationMessage}
          icon={<IconVideo />}
          placeholder={t('video-default-message')}
        />
      )}
    </Stack>
  );
});
